import { Injectable } from "@angular/core";
import { ActionStatus, IDocumentViewerConfig, IViewerConfig, IViewerHandler } from "./viewer.model";
import { TranslocoService } from "@ngneat/transloco";
import { HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, EMPTY, catchError, switchMap, tap } from "rxjs";
import { DmsService, UpLoadProfileEnum } from "app/shared/common/utils/DmsService";
import { UtilCommon } from "app/shared/common/utils/UtilCommon";
import { UtilComponent } from "app/shared/common/utils/UtilComponent";
import { ObjectAccessTokenService } from "app/shared/common/utils/token/ObjectAccessToken.service";
import { FILE_VIEW_DIALOG } from "app/shared/components/file-view-dialog/file-view-dialog.constant";
import { Events } from "app/shared/common/utils/Events";
import { blobToDataURL } from "blob-util";

@Injectable({ providedIn: "root" })
export class DocViewerHandlerService implements IViewerHandler {

    public closeViewer$ = new BehaviorSubject<boolean>(false);
    public loading = new BehaviorSubject<boolean>(false);
    public _config: IDocumentViewerConfig = {
        accessToken: null,
        fileName: null,
        identifier: null,
        permission: {
            download: false
        },
        filePermission: true,
        previewUrl: null,
        typeCode: null,
        index: null
    }
    private callback: (a: ActionStatus) => void;

    constructor(
        public dmsService: DmsService,
        public objectAccessTokenService: ObjectAccessTokenService,
        public utilComponent: UtilComponent,
        private translocoService: TranslocoService
    ) {
    }

    openViewer(input: Omit<IViewerConfig, 'accessToken'>, callback: () => void) {
        this._config.identifier = input.identifier;
        this._config.fileName = input.name;
        this._config.permission = input.permission;
        this.loading.next(true);

        this.verifyAccessToken(input.identifier, (status: ActionStatus) => {
            if (status == ActionStatus.FAILED) {
                callback();
                this.loading.next(false);
                return
            }

            this.loading.next(false)
            this.openFileModalViewer({
                'previewUrl': this._config.previewUrl,
                'fileName': this._config.fileName,
                'identifier': this._config.identifier,
                'accessToken': this._config.accessToken,
                'filePermission': this._config.filePermission,
                'permission': this._config.permission,
                'typeCode': this._config.typeCode
            }, [this._config.identifier])
            callback();
        })
    }

    verifyAccessToken(identifier: string, callback: (status: ActionStatus) => void) {
        this.dmsService.getAccessTokenViewObs([identifier], true)
            .pipe(
                switchMap((result: { access_token: string }) => {
                    if (UtilCommon.isNotNull(result)) {
                        this.objectAccessTokenService.setAccessToken(result.access_token);
                        this._config.accessToken = result.access_token;
                    }
                    return this.dmsService.getFileToView({
                        identifier: this._config.identifier,
                        accessToken: this._config.accessToken,
                        responseType: "blob",
                        preview: true
                    }).pipe(
                        catchError((err) => {
                            callback(ActionStatus.FAILED)
                            this.utilComponent.showTranslateSnackbar('FILE_PREVIEW_NOT_FOUND', 'warning');
                            return EMPTY
                        })
                    )
                }),
            )
            .subscribe({
                next: (result) => {
                    if (UtilCommon.isNotNull(result)) {
                        this._config.previewUrl = new Uint8Array(result);
                    }
                    callback(ActionStatus.SUCCESS)
                },
                error: (err) => {
                    callback(ActionStatus.FAILED)
                    if (err instanceof HttpErrorResponse && err.status == 403) {
                        this.utilComponent.openSnackBarError(this.translocoService.translate('DOCUMENT_LIST.YOU_DONT_HAVE_PERMISSION_TO_SEE_DOCUMENT'));
                    } else {
                        this.utilComponent.showTranslateSnackbar('FILE_PREVIEW_NOT_FOUND', 'warning');
                    }
                }
            });
    }

    openViewerObs(input: Partial<IViewerConfig>, callback?: (status: ActionStatus) => void) {
        this._config.identifier = input.identifier;
        this._config.fileName = input.name;
        this._config.permission = input.permission;
        this._config.typeCode = input.typeCode;
        this._config.index = input.index;
        this.loading.next(true);
        this.callback = callback;
        this.verifyAccessTokenObs(input.identifier, this._config.typeCode);
    }

    verifyAccessTokenObs(identifier: string, typeCode: UpLoadProfileEnum = UpLoadProfileEnum.CERTIFICATE) {
        this.dmsService.downloadAttachment({
            identifier: identifier,
            type: typeCode,
        }, 'arraybuffer').pipe(
            tap(res => {
                if (res === 'blob') {
                } else {
                    blobToDataURL(res).then(dataUrl => {
                        this._config.previewUrl = dataUrl;
                        this.openFileModalViewer({
                            'previewUrl': this._config.previewUrl,
                            'fileName': this._config.fileName,
                            'identifier': this._config.identifier,
                            'accessToken': this._config.accessToken,
                            'filePermission': this._config.filePermission,
                            'permission': this._config.permission,
                            'typeCode': this._config.typeCode,
                            'index': this._config.index
                        }, [this._config.identifier])
                    });
                }
            }),
            catchError(err => {
                if (err instanceof HttpErrorResponse && err.status == 403) {
                    this.utilComponent.openSnackBarError(this.translocoService.translate('DOCUMENT_LIST.YOU_DONT_HAVE_PERMISSION_TO_SEE_DOCUMENT'));
                } else {
                    this.callback(ActionStatus.FAILED);
                    this.utilComponent.openSnackBarError(this.translocoService.translate('FILE_NOT_FOUND'));
                }
                throw Error(err);
            }),
        ).subscribe()
    }

    openFileModalViewer(data: IDocumentViewerConfig, refCodes: string[]) {
        Events.publish(FILE_VIEW_DIALOG.VIEW, data, refCodes);
        Events.subscribe(FILE_VIEW_DIALOG.CLOSE_IFRAME, () => {
            this.objectAccessTokenService.clearData();
            this.closeViewer$.next(true)
        });
    }
}